import { useState, useCallback, useEffect } from 'react';
import { format } from 'date-fns';
import { API_BASE } from '../constants';
import { getStatus } from '../utils';

/**
 * Manages activity row fetching, task fetching, and client-side filtering.
 *
 * Returns:
 *   rows, filtered, loading
 *   date, setDate
 *   search, setSearch
 *   statusFilter, setStatusFilter
 *   fetchActivity
 *   tasks, tasksLoading, fetchTasks
 *   exporting, handleExport
 */
export function useActivity(token) {
  const [rows,    setRows]    = useState([]);
  const [filtered, setFiltered] = useState([]);
  const [loading, setLoading] = useState(false);

  const [date, setDate] = useState(() => {
    const params = new URLSearchParams(window.location.search);
    return params.get('date') || format(new Date(), 'yyyy-MM-dd');
  });

  const [search,       setSearch]       = useState('');
  const [statusFilter, setStatusFilter] = useState('ALL');

  const [tasks,        setTasks]        = useState([]);
  const [tasksLoading, setTasksLoading] = useState(false);

  const [exporting, setExporting] = useState(false);

  // ── Fetch all activity rows ──────────────────────────────────────────────
  const fetchActivity = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(
        `${API_BASE}/api/v1/users/getAllActivity?date=${date}&area=&region=&team=&size=1000&page=1`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const data = await res.json();
      setRows(data.rows || []);
    } catch (e) {
      console.error('Activity fetch failed', e);
    }
    setLoading(false);
  }, [date, token]);

  useEffect(() => { fetchActivity(); }, [fetchActivity]);

  // ── Fetch tasks for a specific user on the active date ───────────────────
  const fetchTasks = useCallback(async (row) => {
    if (!row || !token) return;
    const userId = row.author?._id ?? row.author?.id;
    if (!userId) return;

    const apiDate = format(new Date(`${date}T00:00:00`), 'MM-dd-yyyy');
    setTasks([]);
    setTasksLoading(true);
    try {
      const res = await fetch(
        `${API_BASE}/api/v1/admin/tasks?dateFrom=${apiDate}&dateTo=${apiDate}&userId=${userId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const d = await res.json();
      setTasks(d.entries ?? []);
    } catch {
      setTasks([]);
    } finally {
      setTasksLoading(false);
    }
  }, [token, date]);

  // ── Client-side filter ───────────────────────────────────────────────────
  useEffect(() => {
    let out = rows;

    if (search) {
      const q = search.toLowerCase();
      out = out.filter((r) => {
        const a = r.author;
        return (
          `${a?.firstName} ${a?.lastName}`.toLowerCase().includes(q) ||
          a?.username?.toLowerCase().includes(q) ||
          a?.role?.title?.toLowerCase().includes(q)
        );
      });
    }

    if (statusFilter !== 'ALL') {
      out = out.filter((r) => getStatus(r) === statusFilter);
    }

    setFiltered(out);
  }, [rows, search, statusFilter]);

  return {
    rows, filtered, loading,
    date, setDate,
    search, setSearch,
    statusFilter, setStatusFilter,
    fetchActivity,
    tasks, setTasks, tasksLoading, setTasksLoading, fetchTasks,
    exporting, setExporting,
  };
}
